import type { ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { useStep } from '../state/useStep.js'
import { useDemoStore } from '../state/store.js'
import { cn } from '@/lib/utils.js'

export const ApplianceStatusScreen = (): ReactElement => {
  const { goto } = useStep()
  const { dispatch } = useDemoStore()

  const pickStillInstalled = (): void => {
    dispatch({ type: 'SET_APPLIANCE_STATUS', value: 'still-installed' })
    goto('scan-model-label')
  }

  const pickRemoved = (): void => {
    // Out of scope for this demo. Tell the user politely and stay put.
    alert("This demo only covers the 'still installed' path right now.")
  }

  return (
    <AppShell title="Appliance status" showBack onBack={() => history.back()} progress={0.05}>
      <h1 className="mb-6">What does your dishwasher area look like right now?</h1>

      <Option
        selected
        title="My dishwasher is still installed"
        body="We'll scan the front panel to read the model and check your countertop."
        glyph="▭"
        onClick={pickStillInstalled}
      />
      <Option
        title="It's been removed — the space is empty"
        body="We'll scan the opening to measure dimensions and check the connections."
        glyph="⌷"
        onClick={pickRemoved}
      />
    </AppShell>
  )
}

interface OptionProps {
  selected?: boolean
  title: string
  body: string
  glyph: string
  onClick: () => void
}

const Option = ({ selected, title, body, glyph, onClick }: OptionProps): ReactElement => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'w-full text-left rounded-2xl p-4 mb-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      selected
        ? 'border-2 border-primary bg-primary/5'
        : 'border border-border hover:border-primary/50',
    )}
  >
    <div className="flex gap-3 items-start">
      <div className={cn(
        'w-12 h-12 rounded-xl flex items-center justify-center shrink-0',
        selected
          ? 'bg-background border border-primary/30 text-primary'
          : 'bg-muted text-muted-foreground',
      )}>{glyph}</div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground mt-1">{body}</div>
      </div>
    </div>
  </button>
)
